<script lang="ts">
	import type { Issue, Report } from '#lib/types';
	import { t } from '#lib/i18n/state.svelte';
	import { excerpt } from '#lib/text/normalize';
	import IssueCard from './IssueCard.svelte';

	interface Props {
		report: Report;
		onjump: (issue: Issue) => void;
	}
	let { report, onjump }: Props = $props();

	const access = $derived(report.access);
	const categoryScore = $derived(
		report.scores.categories.find((c) => c.category === 'access')?.score ?? access.score
	);
	const issues = $derived(report.issues.filter((i) => i.category === 'access'));
	let openId = $state<string | null>(null);

	function statusChip(status: Report['access']['checks'][number]['status']): string {
		if (status === 'ok') return 'chip-ok';
		if (status === 'missing') return 'chip-warning';
		if (status === 'invalid') return 'chip-critical';
		return 'chip-neutral';
	}

	function toggle(issue: Issue) {
		openId = openId === issue.id ? null : issue.id;
	}

	function evidence(text: string | undefined): string {
		return text ? excerpt(text, 180) : '';
	}
</script>

<div class="access">
	<section class="panel summary-panel">
		<div class="head">
			<div>
				<p class="eyebrow">{t('access.title')}</p>
				<h2>{t('access.score')}</h2>
				<p class="lead">{t('access.lead')}</p>
			</div>
			<div class="score" style:color={categoryScore >= 80 ? 'var(--ok)' : categoryScore >= 60 ? 'var(--warning)' : 'var(--critical)'}>
				{categoryScore}<span>/100</span>
			</div>
		</div>

		<div class="flags">
			{#if access.humanStudy}<span class="chip chip-neutral">{t('access.human')}</span>{/if}
			{#if access.computational}<span class="chip chip-neutral">{t('access.computational')}</span>{/if}
		</div>

		<h3>{t('access.checksTitle')}</h3>
		<ul class="checks">
			{#each access.checks as check (check.key)}
				<li>
					<span class="check-name">{t(`access.check.${check.key}`)}</span>
					<span class="chip {statusChip(check.status)}">{t(`access.status.${check.status}`)}</span>
					{#if check.status === 'invalid' && check.count}
						<span class="count">×{check.count}</span>
					{/if}
					{#if check.evidence}
						<span class="evidence">{evidence(check.evidence)}</span>
					{/if}
				</li>
			{/each}
		</ul>
		<p class="note">{t('access.note')}</p>
	</section>

	<section class="findings">
		<h3>{t('access.findings', { n: issues.length })}</h3>
		{#if issues.length === 0}
			<p class="empty">{t('access.allGood')}</p>
		{:else}
			<div class="list">
				{#each issues as issue (issue.id)}
					<IssueCard
						{issue}
						expanded={openId === issue.id}
						ontoggle={toggle}
						onjump={onjump}
					/>
				{/each}
			</div>
		{/if}
	</section>
</div>

<style>
	.access {
		display: grid;
		gap: 1rem;
	}
	.panel {
		border: 1px solid var(--line);
		border-radius: var(--radius);
		background: #fff;
		padding: 1.2rem 1.3rem;
		box-shadow: var(--shadow-sm);
	}
	.head {
		display: flex;
		gap: 1rem;
		justify-content: space-between;
		align-items: flex-start;
	}
	.eyebrow {
		margin: 0 0 0.2rem;
		font-size: 0.7rem;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--muted);
		font-weight: 650;
	}
	h2 {
		font-size: 1.1rem;
		margin: 0 0 0.35rem;
	}
	h3 {
		font-size: 0.86rem;
		margin: 1.1rem 0 0.5rem;
		font-family: var(--font-ui);
	}
	.lead {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.86rem;
		max-width: 62ch;
	}
	.score {
		font-family: var(--font-display);
		font-size: 2rem;
		line-height: 1;
		flex: none;
	}
	.score span {
		font-family: var(--font-ui);
		font-size: 0.75rem;
		color: var(--muted);
	}
	.flags {
		display: flex;
		flex-wrap: wrap;
		gap: 0.35rem;
		margin-top: 0.7rem;
	}
	.checks {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.4rem;
	}
	.checks li {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.4rem;
		font-size: 0.84rem;
		border-bottom: 1px dashed var(--line);
		padding-bottom: 0.35rem;
	}
	.check-name {
		font-weight: 600;
		min-width: 9rem;
	}
	.count {
		font-size: 0.72rem;
		color: var(--critical);
	}
	.evidence {
		flex-basis: 100%;
		font-size: 0.74rem;
		color: var(--muted);
		overflow-wrap: anywhere;
	}
	.note {
		margin: 0.7rem 0 0;
		font-size: 0.74rem;
		color: var(--muted);
	}
	.findings h3 {
		margin-top: 0.2rem;
	}
	.empty {
		color: var(--ink-2);
		font-size: 0.86rem;
	}
	.list {
		display: grid;
		gap: 0.55rem;
	}
	@media (max-width: 480px) {
		.panel {
			padding: 1rem;
		}
		.check-name {
			min-width: 7rem;
		}
	}
</style>
